/* ==========================================================================
   Ozeret -- server data into the running app

   The screens import the record arrays directly. Rather than rewrite every
   one of them, we refill those same arrays in place whenever the server
   answers. `seed.ts` stops being seed data and becomes the container the
   live data lives in.
   ========================================================================== */

import type { Bootstrap } from '../api';
import {
  AVAILABILITY, BOOKINGS, CLEANERS, HOUSES, NOTIFICATIONS, OCCURRENCES, RECURRING, REQUESTS,
  REVIEWS_OF_CLEANERS, REVIEWS_OF_HOUSES, THREADS,
} from './seed';
import type { Notification } from '../types';
import { rebuildCustomLabels , shortName } from '../lib/domain';
import { t, tf } from '../lib/i18n';
import { fmtDate } from '../lib/time';

function refillArray<T>(target: T[], next: T[] | undefined): void {
  target.length = 0;
  if (next) target.push(...next);
}

function refillMap(target: Record<string, unknown>, next: Record<string, unknown> | undefined): void {
  for (const k of Object.keys(target)) delete target[k];
  if (next) Object.assign(target, next);
}

/* When each update was last marked read, straight from the server. Kept
   here rather than in the store because the only thing that reads it is the
   step below, which decides what counts as new. */
export const NOTIF_SEEN: Record<string, string> = {};

export function hydrate(b: Bootstrap): void {
  refillArray(CLEANERS as unknown[], b.cleaners);
  refillArray(HOUSES as unknown[], b.houses);
  refillArray(REQUESTS as unknown[], b.requests);
  refillArray(BOOKINGS as unknown[], b.bookings);
  refillArray(RECURRING as unknown[], b.recurring);
  refillArray(OCCURRENCES as unknown[], b.occurrences);
  refillArray(THREADS as unknown[], b.threads);
  refillMap(AVAILABILITY as Record<string, unknown>, b.availability);
  refillMap(REVIEWS_OF_CLEANERS as Record<string, unknown>, b.reviewsOfCleaners);
  refillMap(REVIEWS_OF_HOUSES as Record<string, unknown>, b.reviewsOfHouses);
  refillMap(NOTIF_SEEN as Record<string, unknown>, b.notifSeen);
  rebuildCustomLabels();
  rebuildNotifications(b);
}

/* --------------------------------------------------------- notifications

   Derived rather than stored. Everything here is something that already
   happened in the data — no invented activity. */

function rebuildNotifications(b: Bootstrap): void {
  const house: Notification[] = [];
  const cleaner: Notification[] = [];
  const me = b.me;

  if (me?.role === 'house') {
    for (const r of b.requests) {
      for (const a of r.applicants ?? []) {
        if (a.status === 'declined' || a.status === 'invited') continue;
        const c = b.cleaners.find((x: { id: string }) => x.id === a.cleanerId);
        if (!c) continue;
        house.push({
          id: 'n_' + r.id + '_' + a.cleanerId,
          kind: a.counterRate ? 'counter' : 'applicant',
          text: a.counterRate
            ? tf('tx_n_countered', { name: shortName(c.name), rate: '$' + a.counterRate })
            : tf('tx_n_applied', { name: shortName(c.name), d: fmtDate(r.date) }),
          at: a.at,
          unread: true,
          go: '#/h/request/' + r.id,
        });
      }
    }
    for (const bk of b.bookings) {
      if (bk.status === 'completed' && !bk.iReviewed) {
        const c = b.cleaners.find((x: { id: string }) => x.id === bk.cleanerId);
        house.push({
          id: 'n_rev_' + bk.id,
          kind: 'review',
          text: tf('tx_tell_like', { name: c ? shortName(c.name) : t('tx_they') }),
          at: bk.date + 'T18:00:00',
          unread: true,
          go: '#/h/booking/' + bk.id,
        });
      }
    }
  }

  if (me?.role === 'cleaner') {
    const open = b.requests.filter((r: { status: string }) => r.status === 'open');
    if (open.length) {
      cleaner.push({
        id: 'n_open',
        kind: 'match',
        text: tf(open.length === 1 ? 'tx_homes_near_1' : 'tx_homes_near_n', { n: open.length }),
        at: b.serverTime,
        unread: true,
        go: '#/c/jobs',
      });
    }
    /* A home asked for you by name. */
    for (const r of b.requests) {
      for (const a of r.applicants ?? []) {
        if (a.cleanerId !== me.cleanerId || a.status !== 'invited') continue;
        const h = b.houses.find((x: { id: string }) => x.id === r.houseId);
        cleaner.push({
          id: 'n_inv_' + r.id,
          kind: 'invite',
          text: tf('tx_invited_for', { name: h ? shortName(h.displayName) : t('tx_a_home'), d: fmtDate(r.date) }),
          at: a.at,
          unread: true,
          go: '#/c/job/' + r.id,
        });
      }
    }
    /* A home chose you. */
    for (const bk of b.bookings) {
      if (bk.status !== 'confirmed' || bk.loggedAfterTheFact) continue;
      const h = b.houses.find((x: { id: string }) => x.id === bk.houseId);
      cleaner.push({
        id: 'n_chosen_' + bk.id,
        kind: 'chosen',
        text: tf('tx_booked_for', { name: h ? shortName(h.displayName) : t('tx_a_home'), d: fmtDate(bk.date) }),
        at: bk.createdAt ?? (bk.date + 'T00:00:00'),
        unread: true,
        go: '#/c/booking/' + bk.id,
      });
    }
    for (const bk of b.bookings) {
      if (bk.status === 'completed' && !bk.iReviewed) {
        cleaner.push({
          id: 'n_rev_' + bk.id,
          kind: 'review',
          text: t('tx_review_waiting'),
          at: bk.date + 'T18:00:00',
          unread: true,
          go: '#/c/booking/' + bk.id,
        });
      }
    }
  }

  /* A notice from Ozeret: written by an admin to explain something that
     happened to this person, so it goes to whichever side they are. */
  for (const nt of b.notices ?? []) {
    (me?.role === 'house' ? house : cleaner).push({
      id: 'n_notice_' + nt.id,
      kind: 'notice',
      text: t('tx_from_ozeret') + ' ' + nt.text,
      at: nt.at,
      unread: true,
      go: nt.link || (me?.role === 'house' ? '#/h/requests' : '#/c/jobs'),
    });
  }

  /* Messages are NOT in here. Chats are strictly messaging and the Chats tab
     carries its own count; putting the same unread message on the bell as
     well meant every message was announced twice and neither place could be
     trusted to be quiet. The bell is for things that happened to you. */

  /* Every update above is built with `unread: true`, because building it is
     all this file knows how to do -- it works the list out fresh from the
     state of the world each time, and nothing in that state says whether
     somebody has looked.

     So the answer comes from the read markers instead. An update is new
     unless it has been seen at or after the moment it currently carries.
     Comparing the moment rather than storing a flag is what makes a thread
     with a new message on it go back to being new by itself, while one that
     has not changed stays read. */
  const isNew = (n: Notification) => {
    const seen = NOTIF_SEEN[n.id];
    return !seen || seen < n.at;
  };
  for (const n of [...house, ...cleaner]) n.unread = isNew(n);

  const newest = (a: Notification, z: Notification) => z.at.localeCompare(a.at);
  refillArray(NOTIFICATIONS.house, house.sort(newest));
  refillArray(NOTIFICATIONS.cleaner, cleaner.sort(newest));
}
